/**
 * @module AnalyticsTracker
 * @description Invisible component that batches page views and flushes them
 *              to the analytics API periodically (every 30 s) or on page unload,
 *              instead of firing a request on every single navigation.
 */

import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { postJSON } from '../lib/api';

const FLUSH_INTERVAL_MS = 30_000; // flush every 30 seconds
const MAX_BATCH_SIZE = 20;        // flush if we hit 20 events

// Module-level queue so it survives re-renders but not full page reloads
let eventQueue = [];

function getVisitorId() {
  let id = sessionStorage.getItem('visitorId');
  if (!id) {
    id = `visitor_${Date.now()}_${Math.random()}`;
    sessionStorage.setItem('visitorId', id);
  }
  return id;
}

function flushQueue() {
  if (eventQueue.length === 0) return;

  const batch = eventQueue.splice(0);

  // Use sendBeacon for reliability on page unload; fall back to fetch
  const payload = JSON.stringify({ events: batch });
  const sent = navigator.sendBeacon?.('/api/analytics/batch', new Blob([payload], { type: 'application/json' }));

  if (!sent) {
    postJSON('/api/analytics', batch[0]).catch(() => {});
  }
}

function AnalyticsTracker() {
  const location = useLocation();

  const enqueue = useCallback(() => {
    eventQueue.push({
      visitorId: getVisitorId(),
      page: location.pathname,
      referrer: document.referrer,
      userAgent: navigator.userAgent,
      language: navigator.language,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timestamp: new Date().toISOString(),
    });

    if (eventQueue.length >= MAX_BATCH_SIZE) {
      flushQueue();
    }
  }, [location.pathname]);

  // Enqueue on every route change
  useEffect(() => {
    enqueue();
  }, [enqueue]);

  // Set up periodic flush + flush on unload
  useEffect(() => {
    const timerId = setInterval(flushQueue, FLUSH_INTERVAL_MS);
    const handleUnload = () => flushQueue();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flushQueue();
    };
    window.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handleUnload);

    return () => {
      clearInterval(timerId);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handleUnload);
      flushQueue(); // flush remaining on unmount
    };
  }, []);

  return null;
}

export default AnalyticsTracker;
